import { EVENTS } from "../../../js/constants";

const SELECTORS = {
  FLYOUT: ".js-PaymentAmount-flyout",
  GIFT_PAGE_LINK: ".js-GiftPrompt-link",
  FREQUENCY_INPUT_ID: "edit-spendenbetrag-interval-count",
  AMOUNT_INPUT_ID: "edit-spendenbetrag-donate",
};

const QUERY_PARAMS = {
  FREQUENCY: "frequenz",
  AMOUNT: "betrag",
};

class GiftPrompt {
  /**
   * @param {HTMLElement} element - gift prompt
   */
  constructor(element) {
    this.element = element;
    this.formWrapper = document.getElementById(element.dataset.formWrapperId);
    this.flyout = this.formWrapper.querySelector(SELECTORS.FLYOUT);
    this.giftPageLink = element.querySelector(SELECTORS.GIFT_PAGE_LINK);

    this._addEventListeners();
    this._updateGiftPageLink();
  }

  /**
   * Add event listeners
   */
  _addEventListeners() {
    this.formWrapper.addEventListener(
      "change",
      this._handleFrequencyChange.bind(this)
    );
    this.formWrapper.addEventListener(
      "input",
      this._handleAmountChange.bind(this)
    );

    if (this.flyout) {
      // If the flyout isn't vailable, the 'change' event above handles the gift prompt visibility
      this.flyout.addEventListener(
        EVENTS.FLYOUT_OPEN,
        this._handleFlyoutVisibility.bind(this)
      );
    }
  }

  /**
   * Handle flyout visibility
   *
   * @param {Event} event
   */
  _handleFlyoutVisibility(event) {
    const { detail } = event;

    if (!detail.isOpen) {
      this._toggleVisibility(true);
    }

    if (detail.triggeredElement) {
      this._toggleVisibility(false);
    }
  }

  /**
   * Toggle visibility of the gift prompt
   *
   * @param {boolean} isVisible
   */
  _toggleVisibility(isVisible) {
    this.element.toggleAttribute("hidden", !isVisible);
  }

  /**
   * Handle frequency change
   *
   * @param {Event} event
   */
  _handleFrequencyChange(event) {
    // Ignore change events that are not related to the frequency input
    if (!event.target.id.includes(SELECTORS.FREQUENCY_INPUT_ID)) return;

    this._updateGiftPageLink();

    if (!this.flyout) {
      // If the flyout is available, _handleFlyoutVisibility handles the gift prompt visibility
      this._toggleVisibilityOnFrequencyChange();
    }
  }

  /**
   * Handle amount change
   *
   * @param {Event} event
   */
  _handleAmountChange(event) {
    // Ignore input events that are not related to the amount inputs
    if (!event.target.id.includes(SELECTORS.AMOUNT_INPUT_ID)) return;

    const amount = event.target.value;

    this._updateGiftPageLink(amount);
  }

  /**
   * Toggle visibility of the gift prompt based on the selected frequency
   */
  _toggleVisibilityOnFrequencyChange() {
    const isOneTimeDonation = this._getSelectedFrequency() === "0";
    this._toggleVisibility(isOneTimeDonation);
  }

  /**
   * Get the selected amount
   *
   * @returns {string}
   */
  _getSelectedAmount() {
    const selectedInput = this.formWrapper.querySelector(
      `input[id*="${SELECTORS.AMOUNT_INPUT_ID}"]:checked`
    );

    return selectedInput?.value;
  }

  /**
   * Get the selected frequency
   *
   * @returns {string}
   */
  _getSelectedFrequency() {
    const frequencyInput = this.formWrapper.querySelector(
      `select[id*="${SELECTORS.FREQUENCY_INPUT_ID}"]`
    );

    return frequencyInput?.value;
  }

  /**
   * @param {string} amount
   * Update the page URL with the given frequency and amount
   */
  _updateGiftPageLink(amount) {
    const giftPageUrl = this.giftPageLink.getAttribute("href");

    const frequency = this._getSelectedFrequency();
    const url = new URL(giftPageUrl);

    url.searchParams.set(QUERY_PARAMS.FREQUENCY, frequency);
    url.searchParams.set(
      QUERY_PARAMS.AMOUNT,
      amount || this._getSelectedAmount()
    );

    this.giftPageLink.setAttribute("href", url.toString());
  }
}

export default GiftPrompt;
